<template>
  <div v-if="!busy && cptdClientId" class="GoogleSignInButton">
    <div
      id="g_id_onload"
      :data-client_id="cptdClientId"
      data-context="signin"
      data-ux_mode="popup"
      data-callback="handle"
      data-auto_prompt="false"
    />

    <div
      class="g_id_signin"
      data-type="standard"
      data-shape="pill"
      data-theme="outline"
      data-text="continue_with"
      data-size="large"
      data-locale="pt-BR"
      data-logo_alignment="left"
      data-width="300"
      :data-click-listener="click"
    />
    <component :is="tag" src="https://accounts.google.com/gsi/client" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { Provider } from '@/data/oauth/oauth'
import { parseJwt } from '@/helpers/globalFunctions'
export default {
  name: 'GoogleSignInButton',
  props: {
    context: {
      type: String,
      default: 'login',
    },
    redirect: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['login', 'registered', 'linked'],
  data() {
    return {
      busy: true,
      tag: 'script',
    }
  },
  computed: {
    cptdClientId() {
      return import.meta.env.VITE_OAUTH_GOOGLE_CLIENT_ID
    },
  },
  mounted() {
    window.handle = this.handleSubmit
    this.busy = false
    this.actEmitGaEvent({
      eventCategory: 'google-signin-button',
      eventAction: `button-displayed`,
      eventLabel: `button-displayed`,
    })
  },
  methods: {
    ...mapActions({
      actOAuthLogin: 'oauth/actOAuthLogin',
      actOAuthLink: 'oauth/actOAuthLink',
      createFeedback: 'utils/feedback/createFeedback',
      actEmitGaEvent: 'services/ga/actEmitGaEvent',
    }),
    handleSubmit(response) {
      switch (this.context) {
        case 'login':
          this.login(response.credential)
          break
        case 'register':
          this.register(response.credential)
          break
        case 'link':
          this.link(response.credential)
          break
      }
    },
    async login(accessToken) {
      this.actEmitGaEvent({
        eventCategory: 'google-signin-button',
        eventAction: `login-initiated`,
        eventLabel: `login-initiated`,
      })
      const response = await this.actOAuthLogin({
        provider: Provider.GOOGLE,
        accessToken,
      })
      if (response?.status === 200) {
        this.actEmitGaEvent({
          eventCategory: 'google-signin-button',
          eventAction: `login-finished`,
          eventLabel: `login-finished`,
        })
        this.$emit('login')
      } else if (response?.status === 401) {
        this.actEmitGaEvent({
          eventCategory: 'google-signin-button',
          eventAction: `login-401`,
          eventLabel: `login-401`,
        })
        this.createFeedback({
          text: 'Não encontramos uma conta associada à este email. Cadastre-se ou faça o login utilizando a opção de cpf/email para continuar.',
        })
      }
    },
    register(accessToken) {
      const { given_name, email } = parseJwt(accessToken)
      this.actEmitGaEvent({
        eventCategory: 'google-signin-button',
        eventAction: `register-initiated`,
        eventLabel: `register-initiated`,
      })
      if (this.redirect) {
        sessionStorage.setItem(
          'jb.state.oauth.provider-data',
          JSON.stringify({
            accessToken,
            provider: Provider.GOOGLE,
            name: given_name,
            email,
          }),
        )
        this.$router.push({
          path: '/criar-conta',
        })
        return
      }

      this.$emit('registered', {
        accessToken,
        provider: Provider.GOOGLE,
        name: given_name,
        email,
      })
    },
    async link(accessToken) {
      this.actEmitGaEvent({
        eventCategory: 'google-signin-button',
        eventAction: `link-initiated`,
        eventLabel: `link-initiated`,
      })
      const response = await this.actOAuthLink({
        provider: Provider.GOOGLE,
        accessToken,
      })

      if (response?.status === 200 || response?.status === 401) {
        this.actEmitGaEvent({
          eventCategory: 'google-signin-button',
          eventAction: `link-finished`,
          eventLabel: `link-finished`,
        })
        this.$emit('linked')
        this.createFeedback({
          text: 'Conta atualizada com sucesso. No seu próximo login utilize o botão "Continuar com o google".',
        })
        return
      }

      this.createFeedback({
        text: 'Algo deu errado! Por favor tente novamente.',
      })
      this.actEmitGaEvent({
        eventCategory: 'google-signin-button',
        eventAction: `link-error`,
        eventLabel: `link-error`,
      })
    },
    click() {
      this.actEmitGaEvent({
        eventCategory: 'google-signin-button',
        eventAction: 'button-clicked',
        eventLabel: this.context,
      })
    },
  },
}
</script>

<style lang="scss">
.GoogleSignInButton {
  max-width: 300px;
}
</style>
